/**
 * Shared UX dialogs for the "Series + Groups are the single source of truth"
 * policy (see PRD section on beden/ölçü editing UX friction fix).
 *
 * Three dialogs live here so both `Sizes.jsx` and `MeasurementGroups.jsx`
 * consume the exact same behaviour:
 *
 *  1. `TemplateRenameDialog` — Single-action confirmation. Replaces the older
 *     two-way "propagate vs. isolated" split; the isolated mode was the
 *     source of the friction (fork-and-strand). New policy: rename is global,
 *     if you want a variant use `Seriyi/Grubu Kopyala`.
 *
 *  2. `AddItemDialog` — Adds a size/measure with an OPT-IN per-table picker.
 *     Prevents accidental fan-out to hundreds of unrelated tables.
 *
 *  3. `BlockersDialog` — Shown when the backend returns 409 (either from a
 *     series/group delete or a size/measure delete blocked by non-empty
 *     rows). Lists the offending tables and offers escape hatches
 *     (Kopyala / Vazgeç).
 */
import { useEffect, useMemo, useState } from "react";
import { Lock, ExternalLink, Copy, X, Check, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

/* ============================================================================
 * 1. TemplateRenameDialog — single-action rename confirmation.
 *
 * Props:
 *   state    — { oldName, newName, entityName, entityKind: "series"|"group",
 *                affected: [{id, name}] } or null.
 *   onClose  — called on cancel / after commit.
 *   onSubmit — called with no args; caller runs the rename + closes.
 *   onFork   — optional callback for the "Yeni sürüm oluştur" shortcut;
 *              caller decides how to handle (opens clone dialog usually).
 * ==========================================================================*/
export const TemplateRenameDialog = ({ state, onClose, onSubmit, onFork }) => {
    const [busy, setBusy] = useState(false);
    if (!state) return null;
    const { oldName, newName, entityName, entityKind, affected } = state;
    const noAffected = !affected || affected.length === 0;
    const itemLabel = entityKind === "series" ? "beden" : "ölçü";
    const parentLabel = entityKind === "series" ? "seri" : "grup";

    const commit = async () => {
        setBusy(true);
        try { await onSubmit(); }
        finally { setBusy(false); }
    };

    return (
        <AlertDialog open={!!state} onOpenChange={(o) => !o && !busy && onClose()}>
            <AlertDialogContent data-testid="template-rename-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        &ldquo;{oldName}&rdquo; → &ldquo;{newName}&rdquo; yeniden adlandırılacak
                    </AlertDialogTitle>
                    <AlertDialogDescription asChild>
                        <div>
                            {noAffected ? (
                                <p>
                                    Bu {itemLabel} henüz hiçbir tabloda kullanılmıyor — yeniden adlandırma
                                    sadece <span className="font-medium text-foreground">{entityName}</span>{" "}
                                    içinde uygulanır.
                                </p>
                            ) : (
                                <>
                                    <p>
                                        Bu {itemLabel}i kullanan{" "}
                                        <span className="font-medium text-foreground">{affected.length} tabloda da</span>{" "}
                                        aynı isim güncellenecek.
                                    </p>
                                    <ul className="mt-2 space-y-0.5 max-h-32 overflow-y-auto scroll-thin text-[11.5px] text-muted-foreground rounded-md border border-border bg-muted/20 p-2">
                                        {affected.slice(0, 8).map((t) => (
                                            <li key={t.id}>• {t.name}</li>
                                        ))}
                                        {affected.length > 8 && (
                                            <li className="text-muted-foreground/60">
                                                +{affected.length - 8} tablo daha…
                                            </li>
                                        )}
                                    </ul>
                                    {onFork && (
                                        <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
                                            Sadece bazı tablolarda farklı bir varyasyon istiyorsan,{" "}
                                            <button
                                                type="button"
                                                onClick={onFork}
                                                className="underline decoration-dotted underline-offset-2 text-foreground hover:no-underline"
                                                data-testid="template-rename-fork-shortcut"
                                            >
                                                {parentLabel}yi kopyala
                                            </button>{" "}
                                            ve yeni sürümü kullan.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={busy} data-testid="template-rename-cancel">
                        Vazgeç
                    </AlertDialogCancel>
                    <AlertDialogAction
                        disabled={busy}
                        onClick={(e) => { e.preventDefault(); commit(); }}
                        data-testid="template-rename-confirm"
                    >
                        {busy
                            ? "Uygulanıyor…"
                            : noAffected
                                ? "Yeniden adlandır"
                                : `${affected.length} tabloda güncelle`}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};


/* ============================================================================
 * 2. AddItemDialog — add a size/measure with opt-in per-table picker.
 *
 * Props:
 *   open         — bool
 *   onClose      — close callback
 *   onSubmit     — ({ name, addToTables }) where addToTables is "all" | "none" | string[]
 *   itemKind     — "size" | "measure"
 *   defaultName  — pre-filled input value (from inline typing)
 *   linkedTables — [{id, name}] — tables that source this series/group
 *   existingNames — string[] — names already in this template (for validation)
 * ==========================================================================*/
export const AddItemDialog = ({ open, onClose, onSubmit, itemKind, defaultName = "", linkedTables = [], existingNames = [] }) => {
    const [name, setName] = useState(defaultName);
    // mode = "all" | "none" | "some". When "some", `selected` holds the chosen table IDs.
    const [mode, setMode] = useState("all");
    const [selected, setSelected] = useState(() => new Set());
    const [busy, setBusy] = useState(false);

    // Reset internal state whenever the dialog opens.
    useEffect(() => {
        if (open) {
            setName(defaultName);
            // If no tables use this template, "none" is the only meaningful
            // mode; otherwise default to "all" (most merchants want the new
            // item everywhere the template is used).
            setMode(linkedTables.length === 0 ? "none" : "all");
            setSelected(new Set(linkedTables.map((t) => t.id))); // pre-select all as a hint
        }
    }, [open, defaultName, linkedTables]);

    const trimmed = name.trim();
    const duplicate = trimmed && existingNames.includes(trimmed);
    const canSubmit = !!trimmed && !duplicate && !busy && (mode !== "some" || selected.size > 0);

    const itemLabel = itemKind === "size" ? "beden" : "ölçü";
    const itemLabelUpper = itemKind === "size" ? "Beden" : "Ölçü";

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        try {
            const payload = {
                name: trimmed,
                addToTables:
                    linkedTables.length === 0 ? "none"
                        : mode === "all" ? "all"
                            : mode === "none" ? "none"
                                : Array.from(selected),
            };
            await onSubmit(payload);
        } finally { setBusy(false); }
    };

    const toggleTable = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    return (
        <AlertDialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
            <AlertDialogContent data-testid="add-item-dialog" className="max-w-lg">
                <AlertDialogHeader>
                    <AlertDialogTitle>Yeni {itemLabel} ekle</AlertDialogTitle>
                    <AlertDialogDescription>
                        Bu {itemLabel} önce şablonuna eklenir. Ardından hangi tablolarda görüneceğini seç.
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="space-y-4">
                    <div>
                        <label className="text-[10.5px] font-semibold tracking-wider uppercase text-muted-foreground">
                            {itemLabelUpper} adı
                        </label>
                        <Input
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={itemKind === "size" ? "örn. XXS, 48, 11y" : "örn. Bacak Boyu, Kol Genişliği"}
                            className="mt-1.5 h-9 text-sm bg-background"
                            data-testid="add-item-name-input"
                            onKeyDown={(e) => { if (e.key === "Enter" && canSubmit) { e.preventDefault(); submit(); } }}
                        />
                        {duplicate && (
                            <p className="mt-1 text-[11px] text-destructive">
                                &ldquo;{trimmed}&rdquo; bu şablonda zaten var.
                            </p>
                        )}
                    </div>

                    {linkedTables.length === 0 ? (
                        <div className="rounded-md border border-border bg-muted/20 p-3 text-[11.5px] text-muted-foreground">
                            Bu şablonu henüz hiçbir tablo kullanmıyor — yeni {itemLabel} sadece şablona eklenir.
                        </div>
                    ) : (
                        <div>
                            <div className="text-[10.5px] font-semibold tracking-wider uppercase text-muted-foreground mb-2">
                                Hangi tablolara eklensin?
                            </div>
                            <div className="space-y-1.5" data-testid="add-item-scope-choices">
                                <ScopeOption
                                    id="all"
                                    active={mode === "all"}
                                    onSelect={() => setMode("all")}
                                    title={`Tümüne ekle (${linkedTables.length} tablo)`}
                                    subtitle={`Bu ${itemLabel} her ${linkedTables.length === 1 ? "tabloda" : "tabloda"} yeni bir satır olarak açılır. Ölçüleri sonradan doldurabilirsin.`}
                                    testId="add-item-scope-all"
                                />
                                <ScopeOption
                                    id="none"
                                    active={mode === "none"}
                                    onSelect={() => setMode("none")}
                                    title="Sadece şablona ekle"
                                    subtitle="Hiçbir tabloda satır açılmaz. Yeni tablolar oluşturulurken seçim listesinde görünür."
                                    testId="add-item-scope-none"
                                />
                                <ScopeOption
                                    id="some"
                                    active={mode === "some"}
                                    onSelect={() => setMode("some")}
                                    title="Seç…"
                                    subtitle={mode === "some" ? `${selected.size} / ${linkedTables.length} tablo seçili` : "Belirli tablolara ekle"}
                                    testId="add-item-scope-some"
                                />
                            </div>

                            {mode === "some" && (
                                <div className="mt-2 rounded-md border border-border bg-background max-h-40 overflow-y-auto scroll-thin divide-y divide-border">
                                    {linkedTables.map((t) => (
                                        <label
                                            key={t.id}
                                            className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-muted/30"
                                            data-testid={`add-item-table-${t.id}`}
                                        >
                                            <Checkbox
                                                checked={selected.has(t.id)}
                                                onCheckedChange={() => toggleTable(t.id)}
                                            />
                                            <span className="text-xs truncate">{t.name || t.id}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <AlertDialogFooter>
                    <AlertDialogCancel disabled={busy} data-testid="add-item-cancel">
                        Vazgeç
                    </AlertDialogCancel>
                    <AlertDialogAction
                        disabled={!canSubmit}
                        onClick={(e) => { e.preventDefault(); submit(); }}
                        data-testid="add-item-confirm"
                    >
                        {busy ? "Ekleniyor…" : "Ekle"}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

const ScopeOption = ({ active, onSelect, title, subtitle, testId }) => (
    <label
        onClick={onSelect}
        className={`flex items-start gap-2.5 rounded-md border px-3 py-2.5 cursor-pointer transition-colors ${
            active ? "border-foreground bg-muted/40" : "border-border hover:border-foreground/40"
        }`}
        data-testid={testId}
    >
        <span className={`mt-1 h-2 w-2 rounded-full shrink-0 transition-colors ${active ? "bg-foreground" : "bg-transparent border border-foreground/40"}`} />
        <div className="text-xs leading-relaxed">
            <div className="font-medium text-foreground">{title}</div>
            <div className="text-muted-foreground mt-0.5">{subtitle}</div>
        </div>
    </label>
);


/* ============================================================================
 * 3. BlockersDialog — 409 handler. Called after a delete that the backend
 *    refused because linked tables still hold data. Lists the offending
 *    tables + offers a "Kopyala" shortcut (fork) as the escape hatch.
 * ==========================================================================*/
export const BlockersDialog = ({ state, onClose, onFork }) => {
    if (!state) return null;
    const { title, description, blockers, forkLabel } = state;
    return (
        <AlertDialog open={!!state} onOpenChange={(o) => !o && onClose()}>
            <AlertDialogContent data-testid="blockers-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle className="inline-flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-500" strokeWidth={2} />
                        {title}
                    </AlertDialogTitle>
                    <AlertDialogDescription asChild>
                        <div>
                            {description && <p>{description}</p>}
                            <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto scroll-thin text-[11.5px] text-muted-foreground rounded-md border border-border bg-muted/20 p-2">
                                {(blockers || []).slice(0, 12).map((t) => (
                                    <li key={t.id}>• {t.name || t.id}</li>
                                ))}
                                {(blockers || []).length > 12 && (
                                    <li className="text-muted-foreground/60">
                                        +{(blockers || []).length - 12} tablo daha…
                                    </li>
                                )}
                            </ul>
                        </div>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    {onFork && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 text-xs gap-1.5"
                            onClick={onFork}
                            data-testid="blockers-fork-btn"
                        >
                            <Copy className="h-3 w-3" /> {forkLabel || "Kopyala"}
                        </Button>
                    )}
                    <AlertDialogAction data-testid="blockers-close" onClick={onClose}>
                        Tamam
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};


/* ============================================================================
 * 4. CloneDialog — asks for the new template name before firing the clone.
 * ==========================================================================*/
export const CloneDialog = ({ state, onClose, onSubmit }) => {
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (state) setName(state.defaultName || "");
    }, [state]);
    if (!state) return null;

    const submit = async () => {
        const trimmed = name.trim();
        if (!trimmed) return;
        setBusy(true);
        try { await onSubmit(trimmed); }
        finally { setBusy(false); }
    };

    return (
        <AlertDialog open={!!state} onOpenChange={(o) => !o && !busy && onClose()}>
            <AlertDialogContent data-testid="clone-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>{state.title || "Kopyala"}</AlertDialogTitle>
                    <AlertDialogDescription>{state.description}</AlertDialogDescription>
                </AlertDialogHeader>
                <div>
                    <label className="text-[10.5px] font-semibold tracking-wider uppercase text-muted-foreground">
                        Yeni ad
                    </label>
                    <Input
                        autoFocus
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-1.5 h-9 text-sm bg-background"
                        data-testid="clone-name-input"
                        onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) { e.preventDefault(); submit(); } }}
                    />
                </div>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={busy} data-testid="clone-cancel">
                        Vazgeç
                    </AlertDialogCancel>
                    <AlertDialogAction
                        disabled={busy || !name.trim()}
                        onClick={(e) => { e.preventDefault(); submit(); }}
                        data-testid="clone-confirm"
                    >
                        {busy ? "Oluşturuluyor…" : "Oluştur"}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
